import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  CheckCircle2,
  ShieldAlert,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

// External MR Crusher booking service (separate deployment)
const BOOKING_API =
  process.env.REACT_APP_BOOKING_API_URL ||
  "https://auto-lead-hub-1.emergent.host";
const LOGO_URL = "/logo.jpg";

const TIME_SLOTS = [
  { value: "8am-12pm", label: "8 AM – 12 PM", sub: "Morning" },
  { value: "12pm-4pm", label: "12 PM – 4 PM", sub: "Afternoon" },
  { value: "4pm-8pm", label: "4 PM – 8 PM", sub: "Evening" },
  { value: "8pm-8am", label: "8 PM – 8 AM", sub: "Overnight" },
];

// Local yyyy-mm-dd helper (avoids UTC drift)
const toLocalISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function Appointment() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [offer, setOffer] = useState(null);
  const [preScheduled, setPreScheduled] = useState(null);

  const minDate = useMemo(() => toLocalISODate(new Date()), []);
  const [date, setDate] = useState(minDate);
  const [slot, setSlot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${BOOKING_API}/api/booking/${token}`)
      .then((res) => {
        if (!alive) return;
        const d = res.data || {};
        // Support a few possible response shapes from the booking service
        const amount =
          d.offer_amount ?? d.offerAmount ?? d.offer ?? d.amount ?? null;
        setOffer(amount);
        const scheduled =
          d.already_scheduled ?? d.alreadyScheduled ?? d.scheduled ?? false;
        if (scheduled) {
          setPreScheduled({
            date: d.scheduled_date ?? d.scheduledDate ?? d.date ?? null,
            time: d.scheduled_time ?? d.scheduledTime ?? d.time ?? null,
          });
        }
      })
      .catch((err) => {
        if (!alive) return;
        console.error("booking GET failed", err?.response?.status, err?.response?.data);
        setInvalid(true);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [token]);

  const submit = async () => {
    if (!date) {
      toast.error("Please pick a date.");
      return;
    }
    if (!slot) {
      toast.error("Please pick a time window.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BOOKING_API}/api/booking/${token}/confirm`, {
        date,
        time: slot,
      });
      setDone(true);
    } catch (err) {
      console.error("booking POST failed", err?.response?.status, err?.response?.data);
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Couldn't schedule. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="py-24 text-center text-slate-500" data-testid="appt-loading">
          Loading your offer…
        </div>
      </Shell>
    );
  }

  if (invalid) {
    return (
      <Shell>
        <div
          className="max-w-lg mx-auto text-center py-16"
          data-testid="appt-invalid"
        >
          <div className="mx-auto h-20 w-20 grid place-items-center bg-red-50 border-2 border-red-200 rounded-full text-red-600">
            <ShieldAlert className="h-9 w-9" />
          </div>
          <h1 className="mt-8 font-display font-semibold text-4xl tracking-tight text-slate-900">
            404 · Link invalid
          </h1>
          <p className="mt-4 text-slate-600">
            This appointment link is invalid, expired, or has been revoked.
            Please contact MR Crusher for a fresh link.
          </p>
          <a
            href="tel:+12893004040"
            className="mt-10 inline-flex items-center gap-2 bg-[#0055FF] hover:bg-[#003ECE] text-white px-6 py-3 text-sm font-medium rounded-full transition-colors"
          >
            Call (289) 300-4040
          </a>
        </div>
      </Shell>
    );
  }

  if (done || preScheduled) {
    const shownDate = preScheduled ? preScheduled.date : date;
    const shownSlot = preScheduled ? preScheduled.time : slot;
    return (
      <Shell>
        <div
          className="max-w-lg mx-auto text-center py-16"
          data-testid="appt-success"
        >
          <div className="mx-auto h-20 w-20 grid place-items-center bg-[#0055FF] text-white rounded-full">
            <CheckCircle2 className="h-9 w-9" />
          </div>
          <h1 className="mt-8 font-display font-semibold text-3xl sm:text-4xl tracking-tight text-slate-900">
            Your pickup appointment has been scheduled.
          </h1>
          <p className="mt-4 text-slate-600">
            You'll get a confirmation call from MR Crusher shortly before the
            pickup window.
          </p>
          <div className="mt-10 inline-flex flex-col items-start gap-2 text-left bg-slate-50 border border-slate-200 px-6 py-5 rounded-2xl">
            <Row label="Offer" value={formatCurrency(offer)} />
            <Row label="Date" value={formatDate(shownDate)} />
            <Row label="Window" value={humanSlot(shownSlot)} />
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="max-w-2xl mx-auto py-10 md:py-14">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0055FF]">
          Book pickup
        </div>
        <h1 className="mt-3 font-display font-semibold text-3xl sm:text-4xl md:text-5xl tracking-tight text-slate-900 leading-[1.1]">
          Pick a date & time for pickup
        </h1>

        {/* Offer amount */}
        <div
          className="mt-10 bg-slate-900 text-white px-6 py-6 rounded-2xl flex items-center justify-between"
          data-testid="appt-offer"
        >
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">
              Cash offer
            </div>
            <div className="mt-1 font-display font-semibold text-3xl sm:text-4xl">
              {formatCurrency(offer)}
            </div>
          </div>
          <div className="text-xs font-mono-plex text-slate-400">
            paid at pickup
          </div>
        </div>

        {/* Date picker */}
        <section className="mt-10">
          <label className="text-xs font-medium uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-[#0055FF]" />
            Choose a date
          </label>
          <input
            type="date"
            value={date}
            min={minDate}
            onChange={(e) => setDate(e.target.value)}
            data-testid="appt-date"
            className="mt-3 w-full bg-white border-2 border-slate-300 px-4 py-4 text-base focus:outline-none focus:border-[#0055FF] transition-colors rounded-lg"
          />
        </section>

        {/* Time slots */}
        <section className="mt-8">
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#0055FF]" />
            Choose a time window
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {TIME_SLOTS.map((s) => {
              const active = slot === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setSlot(s.value)}
                  data-testid={`appt-slot-${s.value}`}
                  className={`text-left px-5 py-4 border-2 rounded-2xl transition-all ${
                    active
                      ? "border-[#0055FF] bg-[#0055FF]/5 shadow-sm"
                      : "border-slate-200 bg-white hover:border-slate-900 hover:shadow-sm"
                  }`}
                >
                  <div className="text-base font-semibold text-slate-900">
                    {s.label}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
                    {s.sub}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          data-testid="appt-submit"
          className="mt-12 w-full inline-flex items-center justify-center gap-2 bg-[#0055FF] hover:bg-[#003ECE] disabled:opacity-60 text-white px-8 py-4 font-medium rounded-full transition-colors"
        >
          {submitting ? "Scheduling…" : "Submit appointment"}
          <ArrowRight className="h-4 w-4" />
        </button>

        <p className="mt-4 text-center text-xs text-slate-500">
          Free tow. Cash paid on the spot.
        </p>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="appointment-page">
      <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 md:px-8 h-20 flex items-center justify-between">
          <Link to="/info" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="MR Crusher logo" className="h-12 w-12 object-contain" />
            <span className="font-display font-bold text-lg tracking-tight uppercase text-slate-900 hidden sm:inline">
              MR Crusher
            </span>
          </Link>
          <div className="text-xs font-mono-plex text-slate-500 uppercase tracking-widest">
            Secure link
          </div>
        </div>
      </header>
      <main className="flex-1 px-6 md:px-8">{children}</main>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center gap-4 w-full">
      <span className="text-xs uppercase tracking-widest text-slate-500 w-24">
        {label}
      </span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}

function formatCurrency(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `$${n.toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} CAD`;
}

function humanSlot(v) {
  const m = TIME_SLOTS.find((s) => s.value === v);
  return m ? m.label : v || "—";
}

function formatDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-CA", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
